import * as React from 'react'
import ReactPlayer from 'react-player'
import { ShopifyNext } from '@dotdev/next-components'
import { GlobalProps } from 'types'
import * as Snippets from 'snippets'

const lerp = (x, y, a) => x * (1 - a) + y * a
const easeInOut = (t) => {
  return t > 0.5 ? 4 * Math.pow((t - 1), 3) + 1 : 4 * Math.pow(t, 3)
}
export class DynamicVideo extends React.PureComponent<DynamicVideo.Props, DynamicVideo.State> {
  public static sectionId = 'video'

  constructor(props) {
    super(props)
    this.state = {
      playing: false,
      played: false,
      alternateText: false,
      videoRef: React.createRef(),
      video: null,
      videoDuration: 0,
      pxDuration: 0,
      pxPerSecond: 125,
      currentVideoStart: 0,
      currentVideoEnd: 0,
      currentFrame: 0,
      currentVideoTime: 0,
      playingVideo: false
    }
    this.playVideoOnScroll()
  }

  componentDidMount(): void {
    this.storeVideoRef()
  }

  componentDidUpdate(): void {
    this.storeVideoRef()
  }

  componentWillUnmount(): void {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.storeVideoRef)
  }

  storeVideoRef = () => {
    if (this?.props?.section?.settings?.playwithscroll) {
      const wrapper = this.state.videoRef.current?.wrapper
      if (wrapper) {
        // wait for video duration
        let attempts = 0
        const interval = setInterval(() => {
          if (attempts > 19) {
            clearInterval(interval)
            return
          }
          const video = wrapper.querySelector('video')
          if (video && video.duration) {
            clearInterval(interval)
            this.setState({
              video,
              videoDuration: video.duration,
              pxDuration: video.offsetHeight > window.innerHeight ? video.offsetHeight * 1.5 : window.innerHeight,
            })
          }
          attempts++
        }, 250)
      }
    }
  }

  private playVideoOnScroll() {
    if (this?.props?.section?.settings?.playwithscroll) {
      setInterval(() => {
        this.paintVideoScroll()
      }, 10)
      window.addEventListener('scroll', this.handleScroll)
      window.addEventListener('resize', this.storeVideoRef)
    }
  }

  handleScroll = () => {
    const videoTop = this.state?.video?.getBoundingClientRect().top
    const halfWindowHeight = window.innerHeight / 2
    const offset = this?.props?.section?.settings?.playinstantly ? window.scrollY : (window.innerHeight - videoTop) - (halfWindowHeight * 0.6)
    const newPxVal = offset >= 0 ? offset : 0
    const newTime = ((newPxVal / this.state.pxDuration)) * this.state.videoDuration
    this.setState({
      currentVideoEnd: newTime,
    })
  }

  paintVideoScroll = () => {
    // if current video time greater than current video end, set to previous frame
    // else set to next frame
    if (this.state.video) {
      if (this.state.video.currentTime > this.state.currentVideoEnd) {
        const newTime = this.state.video.currentTime - (1 / 30)
        if (newTime > this.state.currentVideoEnd) {
          this.state.video.currentTime = newTime
        }
      } else if (this.state.video.currentTime < this.state.currentVideoEnd) {
        const newTime = this.state.video.currentTime + (1 / 30)
        if (newTime < this.state.currentVideoEnd) {
          this.state.video.currentTime = newTime
        }
      }
    }
  }

  private setPlayed = (status) => {
    this.setState({
      played: status,
      playing: !status ? false : this.state.playing
    })
  }

  public render(): JSX.Element {
    const { location } = this.props
    const { settings } = this.props.section
    const { playing, played, videoRef } = this.state
    if (!settings) return null
    const url = window.innerWidth > 1024 ? settings.videourl : settings.mobilevideourl
    const image = window.innerWidth > 1024 ? settings.image : settings.imagemobile
    const ratio = window.innerWidth > 1024 || settings.keepdesktopratio ? (settings.playwithscroll ? '1:2' : '16:9') : (settings.playwithscroll ? '1:1' : '11:20')
    const margins = settings.maxwidth === 'Align with Footer' ? 'mx-1-6 sm:mx-2-4 xl:mx-22 md:mx-2-8' : undefined
    const maxWidth = settings.maxwidth !== 'Full' && settings.maxwidth !== 'Align with Footer' ? settings.maxwidth : undefined

    return (
      <section className={`dynamic-video ${location.pathname.includes('40th-birthday') ? 'px-1-6' : ''}`}>
        <div style={{ maxWidth }} className={`${!maxWidth && 'max-w-container'} ${margins || 'mx-auto'} relative`}>
          <ReactPlayer
            url={url}
            ref={videoRef}
            playing={this.state.playing}
            playsinline={true}
            loop={false}
            width={'100%'}
            height={'100%'}
            onPlay={() => {
              this.setPlayed(true)
            }}
            onEnded={() => {
              this.setPlayed(false)
            }}
            style={{
              width: '100%',
              position: 'absolute',
              top: '0px',
              right: '0px',
              bottom: '0px',
              left: '0px',
            }}
          />

          <div
            className={`absolute inset-0 flex p-2 md:p-4 items-end justify-end z-10 transition-opacity cursor-pointer ${settings?.playwithscroll ? 'hidden' : ''}`}
            onClick={() => this.toggleVideo()}
          >
            <div className={'text-white'}>
              <Snippets.Button
                title={'Watch Video'}
                colour={'icon'}
                className={'border bg-white border-black'}
              >
                <Snippets.Icon
                  className={'text-black'}
                  width={16}
                  height={16}
                  name={this.state.playing ? 'pause' : 'play'}
                />
              </Snippets.Button>
            </div>
          </div>

          <div
            className={'absolute w-full bottom-0 right-0 flex items-center justify-center z-10 cursor-pointer'}
          >
            <div className={'text-white'}>
              <Snippets.Button
                title={this.state.alternateText ? 'Hide video text description' : 'Display video text description'}
                colour={'primary'}
                className="sr-only focus:not-sr-only"
                onClick={() => this.toggleAlternateText()}
              >
                <span className="py-1">
                  {this.state.alternateText ? 'Hide video text description' : 'Display video text description'}
                </span>
              </Snippets.Button>
            </div>
          </div>
          <Snippets.Image
            alt={'Video poster image'}
            ratio={ratio}
            className={`absolute inset-0 w-full ${playing ? 'opacity-0' : 'opacity-100'}`}
            src={image}
            preventLazy
          />
        </div>
        <div className={`${this.state.alternateText ? 'h-full' : 'h-0'} overflow-hidden max-w-2xl mx-auto`}>
          <Snippets.RichText className="py-2">
            {settings?.alternativetext?.replace(/(?:\r\n|\r|\n)/g, '<br />')}
          </Snippets.RichText>
        </div>
      </section>
    )
  }

  private toggleVideo = (): void => {
    this.setState((prevState) => ({
      playing: !prevState.playing
    }))
  }

  private toggleAlternateText = (): void => {
    this.setState((prevState) => ({
      alternateText: !prevState.alternateText
    }))
  }
}

export namespace DynamicVideo {
  export type Options = ShopifyNext.Section<{
    image: string;
    imagemobile: string;
    playwithscroll: boolean;
    playinstantly: boolean;
    videourl: string;
    mobilevideourl: string;
    alternativetext: string;
    maxwidth: string;
    keepdesktopratio: boolean;
    showcontrols: boolean;
  }>;
  export interface Props extends GlobalProps {
    section: DynamicVideo.Options;
    location: any;
  }
  export interface State {
    playing: boolean;
    played: boolean;
    alternateText: boolean;
    videoRef: any;
    video: any;
    videoDuration: number;
    pxPerSecond: number;
    pxDuration: number;
    currentVideoStart: number;
    currentVideoEnd: number;
    currentVideoTime: number;
    currentFrame: number;
    playingVideo: boolean;
  }
}
